import React, { useEffect, useState } from 'react';
import {
  ActivityIndicator,
  Image,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  StatusBar,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import LinearGradient from 'react-native-linear-gradient';
import Icon from 'react-native-vector-icons/Feather';
import { useNavigation, useRoute } from '@react-navigation/native';
import type { NativeStackNavigationProp, NativeStackScreenProps } from '@react-navigation/native-stack';
import { applyActionCode } from 'firebase/auth';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { auth } from '../firebase';
import { Colors, Radius, Shadow, Typography } from '../theme';
import type { RootStackParamList } from '../types';
import { useI18n } from '../i18n';

// Mirrors ResetPasswordScreen.tsx's oobCode handling exactly — this is the
// email-verification counterpart of that same always-mounted, deep-link-
// reachable screen (registered at the bottom of AppNavigator, outside every
// conditional branch, so it resolves regardless of auth/onboarding state).
// The emailed link points here (see functions/index.js's
// requestEmailVerificationEmailHandler, which rebuilds Firebase's generated
// link onto https://tonyte.app/verify-email via the same buildTonyteActionLink
// helper used for password resets) instead of Firebase's generic hosted
// action page, so tapping it reopens the app and immediately reflects the
// new verified status — no separate "check again" round trip required.
type Props = NativeStackScreenProps<RootStackParamList, 'VerifyEmailComplete'> & {
  onCheckEmailVerification: () => Promise<boolean>;
  onContinueToLogin: () => Promise<void>;
};

type Status = 'verifying' | 'success' | 'error';

const logo = require('../assets/login_logo.png');

const getErrorMessage = (error: unknown, t: (key: string) => string) => {
  const code = typeof error === 'object' && error !== null && 'code' in error
    ? String((error as { code?: unknown }).code || '')
    : '';

  switch (code) {
    case 'auth/expired-action-code':
      return t('verifyEmail.expiredLink');
    case 'auth/invalid-action-code':
      return t('verifyEmail.invalidLink');
    case 'auth/network-request-failed':
      return t('verifyEmail.networkError');
    default:
      return t('verifyEmail.verifyFailed');
  }
};

export default function VerifyEmailCompleteScreen({ onCheckEmailVerification, onContinueToLogin }: Props) {
  const insets = useSafeAreaInsets();
  const { t } = useI18n();
  const route = useRoute<Props['route']>();
  const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const oobCode = route.params?.oobCode?.trim() || '';
  const [status, setStatus] = useState<Status>('verifying');
  const [message, setMessage] = useState('');

  useEffect(() => {
    let cancelled = false;

    if (!oobCode) {
      setStatus('error');
      setMessage(t('verifyEmail.missingCode'));
      return undefined;
    }

    applyActionCode(auth, oobCode)
      .then(async () => {
        // Best-effort — only meaningfully updates state if the account
        // currently signed in on this device is the one that was just
        // verified (the common case: same device, still signed in from
        // signup). A link opened elsewhere still fully completes the
        // verification server-side either way.
        await onCheckEmailVerification().catch(() => undefined);
        if (cancelled) return;
        setStatus('success');
      })
      .catch(error => {
        if (cancelled) return;
        setStatus('error');
        setMessage(getErrorMessage(error, t));
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [oobCode]);

  const continueToApp = async () => {
    await onContinueToLogin().catch(() => undefined);
    if (navigation.canGoBack()) {
      navigation.popToTop();
    }
  };

  return (
    <LinearGradient colors={['#0B1826', '#070B14', '#020409']} style={styles.container}>
      <StatusBar barStyle="light-content" translucent backgroundColor="transparent" />
      <KeyboardAvoidingView style={styles.flex} behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
        <ScrollView
          contentContainerStyle={[styles.scroll, { paddingTop: insets.top + 36, paddingBottom: insets.bottom + 28 }]}
          keyboardShouldPersistTaps="handled"
        >
          <Image source={logo} style={styles.logo} resizeMode="contain" />
          <Text style={styles.brand}>TONYTE</Text>
          <View style={styles.card}>
            {status === 'verifying' ? (
              <View style={styles.stateWrap}>
                <ActivityIndicator size="large" color="#FF7A00" />
                <Text style={styles.stateText}>{t('verifyEmail.checking')}</Text>
              </View>
            ) : status === 'success' ? (
              <View style={styles.stateWrap}>
                <View style={[styles.stateIcon, styles.successIcon]}>
                  <Icon name="check" size={34} color="#86EFAC" />
                </View>
                <Text style={styles.successTitle}>{t('verifyEmail.verifiedSuccess')}</Text>
                <TouchableOpacity style={styles.primaryButton} onPress={() => void continueToApp()} activeOpacity={0.84}>
                  <Text style={styles.primaryButtonText}>{t('common.continue')}</Text>
                </TouchableOpacity>
              </View>
            ) : (
              <View style={styles.stateWrap}>
                <View style={[styles.stateIcon, styles.errorIcon]}>
                  <Icon name="x" size={34} color="#FDA4AF" />
                </View>
                <Text style={styles.errorTitle}>{t('verifyEmail.unavailable')}</Text>
                <Text style={styles.errorText}>{message}</Text>
                <TouchableOpacity style={styles.secondaryButton} onPress={() => void continueToApp()} activeOpacity={0.84}>
                  <Text style={styles.secondaryButtonText}>{t('common.continue')}</Text>
                </TouchableOpacity>
              </View>
            )}
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
    </LinearGradient>
  );
}

const styles = StyleSheet.create({
  flex: { flex: 1 },
  container: { flex: 1, backgroundColor: Colors.bg },
  scroll: {
    flexGrow: 1,
    justifyContent: 'center',
    paddingHorizontal: 20,
  },
  logo: {
    width: 82,
    height: 82,
    alignSelf: 'center',
  },
  brand: {
    marginTop: 8,
    marginBottom: 22,
    color: '#FF7A00',
    fontSize: Typography.xs,
    fontWeight: Typography.extrabold,
    letterSpacing: 4,
    textAlign: 'center',
  },
  card: {
    width: '100%',
    maxWidth: 430,
    alignSelf: 'center',
    padding: 24,
    borderRadius: 30,
    borderWidth: 1,
    borderColor: Colors.border,
    backgroundColor: 'rgba(9,17,29,0.92)',
    ...Shadow.strong,
  },
  stateWrap: {
    minHeight: 220,
    alignItems: 'center',
    justifyContent: 'center',
    paddingTop: 24,
  },
  stateText: {
    marginTop: 16,
    color: Colors.textSecondary,
    fontSize: Typography.sm,
  },
  stateIcon: {
    width: 68,
    height: 68,
    borderRadius: 24,
    alignItems: 'center',
    justifyContent: 'center',
  },
  successIcon: {
    backgroundColor: 'rgba(34,197,94,0.13)',
  },
  errorIcon: {
    backgroundColor: 'rgba(244,63,94,0.13)',
  },
  successTitle: {
    marginTop: 20,
    color: Colors.textPrimary,
    fontSize: Typography.lg,
    fontWeight: Typography.bold,
    textAlign: 'center',
  },
  errorTitle: {
    marginTop: 20,
    color: Colors.textPrimary,
    fontSize: Typography.lg,
    fontWeight: Typography.bold,
  },
  errorText: {
    marginTop: 10,
    color: Colors.textSecondary,
    fontSize: Typography.sm,
    lineHeight: 21,
    textAlign: 'center',
  },
  primaryButton: {
    width: '100%',
    minHeight: 52,
    marginTop: 22,
    borderRadius: Radius.lg,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#FF7A00',
    ...Shadow.glow,
  },
  primaryButtonText: {
    color: '#fff',
    fontSize: Typography.base,
    fontWeight: Typography.extrabold,
  },
  secondaryButton: {
    width: '100%',
    minHeight: 52,
    marginTop: 22,
    borderRadius: Radius.lg,
    borderWidth: 1,
    borderColor: Colors.border,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255,255,255,0.05)',
  },
  secondaryButtonText: {
    color: Colors.textPrimary,
    fontSize: Typography.base,
    fontWeight: Typography.bold,
  },
});
